/**
 * DOM Labels System
 * Remplace les pseudo-éléments CSS par de vrais éléments DOM repositionnables
 */

import {
  TEMPLATE_SYNC_ELEMENT_CLASS,
  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
  TEMPLATE_SYNC_ELEMENT_IDs,
  TEMPLATE_PART_ELEMENT_PART_TYPE,
  TEMPLATE_SECTION_IDENTIFIER,
} from './constants';
// eslint-disable-next-line import/no-cycle
import { isTeaserTemplate } from './helpers';
// eslint-disable-next-line import/no-cycle
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';

interface LabelElement extends HTMLElement {
  _spmLabelFor?: string;
}

// Tracking arrays for proper cleanup
const activeObservers: MutationObserver[] = [];
const activeElementListeners: { element: HTMLElement; type: string; handler: EventListener }[] = [];
const observedElements = new WeakSet<HTMLElement>();
const repositionWindows = new WeakSet<Window>();
const repositionWindowListeners: { win: Window; type: string; handler: EventListener }[] = [];

// SVG icons for state badges (inline to avoid external dependencies)
const BADGE_SYNC_ICON = '<svg class="spm-badge-icon" viewBox="0 0 24 24"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg>';
const BADGE_STYLE_ICON = '<svg class="spm-badge-icon" viewBox="0 0 24 24"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>';
const BADGE_CONDITIONAL_ICON = '<svg class="spm-badge-icon" viewBox="0 0 24 24"><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/></svg>';
const BADGE_LOOP_ICON = '<svg class="spm-badge-icon" viewBox="0 0 24 24"><path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></svg>';

/**
 * Ajoute les badges d'état au wrapper en fonction des classes/attributs de l'élément
 */
function appendStateBadges(wrapper: HTMLElement, element: HTMLElement): void {
  const doc = element.ownerDocument;

  const hasSyncElement = element.classList.contains(TEMPLATE_SYNC_ELEMENT_CLASS)
    && !element.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE);
  const hasStyleSync = element.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)
    && element.getAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE) === 'styles';
  const hasConditional = element.hasAttribute('s-segments');
  const hasLoop = element.classList.contains('spm_loop');

  if (hasSyncElement) {
    const badge = doc.createElement('span');
    badge.className = 'spm-state-badge spm-badge-sync';
    badge.innerHTML = `${BADGE_SYNC_ICON}<span class="spm-badge-text">Synchronisé</span>`;
    wrapper.appendChild(badge);
  }

  if (hasStyleSync) {
    const badge = doc.createElement('span');
    badge.className = 'spm-state-badge spm-badge-style';
    badge.innerHTML = `${BADGE_STYLE_ICON}<span class="spm-badge-text">Style synchronisé</span>`;
    wrapper.appendChild(badge);
  }

  if (hasConditional) {
    const badge = doc.createElement('span');
    badge.className = 'spm-state-badge spm-badge-conditional';
    badge.innerHTML = `${BADGE_CONDITIONAL_ICON}<span class="spm-badge-text">Condition</span>`;
    wrapper.appendChild(badge);
  }

  if (hasLoop) {
    const badge = doc.createElement('span');
    badge.className = 'spm-state-badge spm-badge-loop';
    badge.innerHTML = `${BADGE_LOOP_ICON}<span class="spm-badge-text">Répétition</span>`;
    wrapper.appendChild(badge);
  }
}

/**
 * Crée un wrapper spm-label-wrapper unifié pour tous les éléments, avec badges d'état si applicable
 */
function createLabelWrapper(text: string, element: HTMLElement): HTMLElement {
  // Supprimer le wrapper existant s'il y en a un
  const existingWrapper = element.querySelector('.spm-label-wrapper');
  if (existingWrapper) {
    existingWrapper.remove();
  }

  // Créer le wrapper unifié
  const wrapper = element.ownerDocument.createElement('div');
  wrapper.className = 'spm-label-wrapper';
  // Integration widgets get a distinct colour to signal they come from an
  // integration (styled in baseIframe.css).
  if (element.classList.contains('spm_widget_integration')) {
    wrapper.classList.add('spm-label-integration');
  }

  // Créer le label texte principal
  const labelSpan = element.ownerDocument.createElement('span');
  labelSpan.className = 'spm-label-text';
  labelSpan.textContent = text;
  wrapper.appendChild(labelSpan);

  // Ajouter les badges d'état si l'élément a des classes/attributs correspondants
  appendStateBadges(wrapper, element);

  return wrapper;
}

/**
 * Gère l'affichage/masquage du label DOM avec cycle create/delete et positionnement instantané
 */
function updateDOMLabel(element: HTMLElement, shouldShow: boolean): void {
  // SYSTÈME UNIFIÉ : Utiliser UNIQUEMENT spm-label-wrapper pour tous les éléments
  const existingWrapper = element.querySelector('.spm-label-wrapper') as HTMLElement;

  // Teaser templates have very small widgets; the name label collides with the
  // edit toolbar and makes the UI unusable. Skip labels entirely on teaser.
  if (isTeaserTemplate(TemplateEditorState.template?.type)) {
    if (existingWrapper) existingWrapper.remove();
    return;
  }

  // Skip label display if element is flagged as hidden (e.g. when hovering another widget)
  if (shouldShow && element.hasAttribute('data-spm-label-hidden')) {
    return;
  }

  if (shouldShow) {
    if (!existingWrapper) {
      // Créer le wrapper spm-label-wrapper pour tous les éléments
      const labelText = element.getAttribute('data-spm-elementtype-text');
      if (labelText) {
        const newWrapper = createLabelWrapper(labelText, element);
        element.appendChild(newWrapper);
        // Repositionner intelligemment
        setTimeout(() => {
          repositionLabelWrapper(newWrapper);
        }, 50);
      }
    } else {
      // Wrapper existant, le repositionner
      setTimeout(() => {
        repositionLabelWrapper(existingWrapper);
      }, 50);
    }
  } else {
    // MASQUER au mouseout - supprimer le wrapper créé à la volée
    // Garder seulement les wrappers avec badges (états spéciaux)
    if (existingWrapper && !hasStateBadges(existingWrapper)) {
      existingWrapper.remove();
    }
  }
}

/**
 * Vérifie si un wrapper contient des badges d'état
 */
function hasStateBadges(wrapper: HTMLElement): boolean {
  return wrapper.querySelector('.spm-state-badge') !== null;
}

/**
 * Repositionne un wrapper spm-label-wrapper existant en calculant directement
 * la meilleure position (pas de test pass/fail successif).
 */
function repositionLabelWrapper(wrapper: HTMLElement): void {
  const optimalClass = calculateOptimalWrapperPosition(wrapper);

  wrapper.classList.remove('spm-label-right', 'spm-label-bottom-left', 'spm-label-bottom-right');

  if (optimalClass !== 'default') {
    wrapper.classList.add(optimalClass);
  }

  // Attach scroll/resize listeners once per window so labels follow layout
  // changes (scroll inside iframe, panel resize, etc.) instead of staying stale.
  ensureRepositionListeners(wrapper.ownerDocument.defaultView);
}

/**
 * Attach scroll and resize listeners to the given window (iframe or main)
 * once. On each event, reposition every visible .spm-label-wrapper in that
 * document.
 */
function ensureRepositionListeners(win: Window | null): void {
  if (!win || repositionWindows.has(win)) return;
  repositionWindows.add(win);

  let rafId: number | null = null;
  const schedule = () => {
    if (rafId !== null) return;
    rafId = win.requestAnimationFrame(() => {
      rafId = null;
      const wrappers = win.document.querySelectorAll<HTMLElement>('.spm-label-wrapper');
      wrappers.forEach((wrapper) => {
        const parent = wrapper.parentElement;
        if (parent && (parent.classList.contains('spm_hover') || parent.classList.contains('spm_active_element'))) {
          const optimalClass = calculateOptimalWrapperPosition(wrapper);
          wrapper.classList.remove('spm-label-right', 'spm-label-bottom-left', 'spm-label-bottom-right');
          if (optimalClass !== 'default') {
            wrapper.classList.add(optimalClass);
          }
        }
      });
    });
  };

  win.addEventListener('scroll', schedule, { passive: true, capture: true });
  win.addEventListener('resize', schedule, { passive: true });

  repositionWindowListeners.push(
    { win, type: 'scroll', handler: schedule as EventListener },
    { win, type: 'resize', handler: schedule as EventListener },
  );
}

/**
 * Compute the optimal wrapper position by scoring the space available around
 * the parent element in the iframe viewport. Deterministic preference order:
 * default (top-left) > spm-label-right (top-right) > spm-label-bottom-left
 * (bottom-left) > spm-label-bottom-right (bottom-right).
 */
function calculateOptimalWrapperPosition(wrapper: HTMLElement): 'default' | 'spm-label-right' | 'spm-label-bottom-left' | 'spm-label-bottom-right' {
  const parent = wrapper.parentElement;
  if (!parent) return 'default';

  const ownerWindow = wrapper.ownerDocument.defaultView;
  if (!ownerWindow) return 'default';

  // Iframe viewport (or main window). Using ownerDocument.defaultView ensures
  // we compare against the same coordinate space as getBoundingClientRect().
  const viewportW = ownerWindow.innerWidth;
  const viewportH = ownerWindow.innerHeight;

  const parentRect = parent.getBoundingClientRect();

  // If parent is entirely outside the viewport, fall back to default — the
  // label is hidden anyway, no need to reposition.
  if (parentRect.bottom <= 0 || parentRect.top >= viewportH
    || parentRect.right <= 0 || parentRect.left >= viewportW) {
    return 'default';
  }

  // Measure the wrapper's intrinsic size. First call may be 0×0 if the wrapper
  // is still display:none (visibility driven by :has() CSS on the parent). Fall
  // back to conservative defaults in that case.
  const wrapperRect = wrapper.getBoundingClientRect();
  const labelW = wrapperRect.width > 0 ? wrapperRect.width : 140;
  const labelH = wrapperRect.height > 0 ? wrapperRect.height : 28;

  // Gaps matching the CSS (4px default, 8px for other positions).
  const GAP = 8;

  // Vertical fit: is there room above / below parent inside the viewport?
  const fitsTop = parentRect.top - labelH - GAP >= 0;
  const fitsBottom = parentRect.bottom + labelH + GAP <= viewportH;

  // Horizontal fit when anchored to parent's left edge vs right edge.
  const fitsLeftAligned = parentRect.left >= 0 && parentRect.left + labelW <= viewportW;
  const fitsRightAligned = parentRect.right <= viewportW && parentRect.right - labelW >= 0;

  if (fitsTop && fitsLeftAligned) return 'default';
  if (fitsTop && fitsRightAligned) return 'spm-label-right';
  if (fitsBottom && fitsLeftAligned) return 'spm-label-bottom-left';
  if (fitsBottom && fitsRightAligned) return 'spm-label-bottom-right';

  // No position fully fits. Prefer default — if parent has any visible area we
  // accept a slightly clipped label over a systematic bottom-right fallback.
  return 'default';
}

/**
 * Initialise les labels DOM pour tous les éléments appropriés.
 * Note : les labels sont créés à la volée sur hover/active via setupElementObserver.
 * initDOMLabels peut s'exécuter avant que l'iframe soit injectée ; dans ce cas
 * le DOM main-document ne contient rien, et addDOMLabelToNewElement prend le relais
 * au fur et à mesure que les éléments sont décorés.
 */
export function initDOMLabels(): void {
  const selectors = [
    '.spm_section',
    '.spm_draggable_widget',
    '.spm_droppable_widget',
  ];

  selectors.forEach(selector => {
    const elements = document.querySelectorAll<HTMLElement>(selector);
    elements.forEach(element => {
      setupElementObserver(element);
    });
  });
}

/**
 * Configure l'observer pour un élément (fonction utilitaire)
 */
function setupElementObserver(element: HTMLElement): void {
  // Prevent duplicate observers on the same element
  if (observedElements.has(element)) return;
  observedElements.add(element);

  // MutationObserver avec debounce adaptatif et stabilisation
  let updateTimer: number | null = null;
  let consecutiveHideCount = 0; // Compteur pour éviter suppressions rapides
  let lastMousePosition: { x: number; y: number } | null = null;
  let mouseStillInElement = false;
  const elementType = element.tagName.toLowerCase();
  const isTable = elementType === 'table' || element.closest('table') !== null;

  // Délai adaptatif : plus long pour les tables (mutations fréquentes)
  const debounceDelay = isTable ? 300 : 50;
  const stabilityThreshold = isTable ? 5 : 1;

  // SOLUTION ALTERNATIVE POUR TABLES : Tracker position souris directement
  if (isTable) {
    const trackMousePosition = (e: MouseEvent) => {
      lastMousePosition = { x: e.clientX, y: e.clientY };
      const rect = element.getBoundingClientRect();
      mouseStillInElement = (
        e.clientX >= rect.left && e.clientX <= rect.right
        && e.clientY >= rect.top && e.clientY <= rect.bottom
      );
    };
    const mouseLeaveHandler = () => {
      mouseStillInElement = false;
      lastMousePosition = null;
    };

    element.addEventListener('mousemove', trackMousePosition);
    element.addEventListener('mouseenter', trackMousePosition);
    element.addEventListener('mouseleave', mouseLeaveHandler);

    activeElementListeners.push(
      { element, type: 'mousemove', handler: trackMousePosition as EventListener },
      { element, type: 'mouseenter', handler: trackMousePosition as EventListener },
      { element, type: 'mouseleave', handler: mouseLeaveHandler as EventListener },
    );
  }

  const observer = new MutationObserver((mutations) => {
    const hasSyncStateMutation = mutations.some(
      (mutation) =>
        mutation.type === 'attributes'
        && !!mutation.attributeName
        && [
          TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
          TEMPLATE_SYNC_ELEMENT_IDs,
          TEMPLATE_PART_ELEMENT_PART_TYPE,
        ].includes(mutation.attributeName),
    );

    // Force fresh label rebuild after unlink/sync-state updates.
    if (hasSyncStateMutation) {
      const staleWrapper = element.querySelector('.spm-label-wrapper');
      if (staleWrapper) {
        staleWrapper.remove();
      }
    }

    if (updateTimer) {
      clearTimeout(updateTimer);
    }

    updateTimer = window.setTimeout(() => {
      let shouldShow;

      // LOGIQUE ALTERNATIVE POUR TABLES : Utiliser position souris au lieu des classes instables
      if (isTable && lastMousePosition !== null) {
        shouldShow = mouseStillInElement || element.classList.contains('spm_active_element');
      } else {
        shouldShow = element.classList.contains('spm_hover')
          || element.classList.contains('spm_active_element');
      }

      if (shouldShow) {
        consecutiveHideCount = 0;
        updateDOMLabel(element, true);
      } else {
        if (isTable && mouseStillInElement) {
          return;
        }

        consecutiveHideCount++;

        if (consecutiveHideCount >= stabilityThreshold) {
          updateDOMLabel(element, false);
          consecutiveHideCount = 0;
        }
      }

      updateTimer = null;
    }, debounceDelay);
  });

  observer.observe(element, {
    attributes: true,
    attributeFilter: [
      'class',
      TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
      TEMPLATE_SYNC_ELEMENT_IDs,
      TEMPLATE_PART_ELEMENT_PART_TYPE,
    ],
  });

  activeObservers.push(observer);
}

/**
 * Configure un nouvel élément pour le système de labels DOM
 */
export function addDOMLabelToNewElement(element: HTMLElement): void {
  setupElementObserver(element);

  // Si l'élément a déjà les classes hover/active, créer le label immédiatement
  const hasHoverOrActive = element.classList.contains('spm_hover')
    || element.classList.contains('spm_active_element');

  if (hasHoverOrActive) {
    updateDOMLabel(element, true);
  }
}

/**
 * Nettoie le système de labels DOM
 */
export function cleanupDOMLabels(): void {
  activeObservers.forEach(obs => obs.disconnect());
  activeObservers.length = 0;

  activeElementListeners.forEach(({ element, type, handler }) => {
    element.removeEventListener(type, handler);
  });
  activeElementListeners.length = 0;

  // Remove scroll/resize listeners attached to windows for repositioning
  repositionWindowListeners.forEach(({ win, type, handler }) => {
    try {
      win.removeEventListener(type, handler, type === 'scroll' ? { capture: true } : undefined);
    } catch {
      // window may already be gone (iframe detached)
    }
  });
  repositionWindowListeners.length = 0;

  const wrappers = document.querySelectorAll('.spm-label-wrapper');
  wrappers.forEach(w => w.remove());

  // Legacy cleanup
  const labels = document.querySelectorAll('.spm-dom-label');
  labels.forEach(label => label.remove());
}
